import { useEffect } from 'react'
import { useParams, useNavigate } from 'react-router'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import type { CatalogGear, GearLayout, ProceduralShape } from '@hangarmax/types'
import { useAdminCatalogItem, useCreateCatalogItem, useUpdateCatalogItem } from '@/hooks/useAdminCatalog'
import { PageHeader } from '@/components/shared/PageHeader'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import { Skeleton } from '@/components/ui/skeleton'
import { ArrowLeft, Loader2, Save } from 'lucide-react'
import { Link } from 'react-router'
import { CLASS_TIER, TIER_LABELS } from '@/lib/tierLadder'
import { AIRCRAFT_CLASSES, AIRCRAFT_CLASS_LABEL, SHAPE_LABEL } from '@/lib/sceneLabels'

// Type-only: this shape is never `.parse()`d, it just derives FormValues below.
// The underscore keeps it past no-unused-vars and marks the fact that catalog
// edits are NOT runtime-validated against it.
const _schema = z.object({
  asset_code: z.string().min(1),
  display_name: z.string().min(1),
  name: z.string().min(1),
  manufacturer: z.string().nullable(),
  source: z.string().default('hangarfit'),
  category: z.enum(['aircraft', 'vehicle', 'equipment', 'furniture', 'structural', 'custom']),
  subcategory: z.string().nullable(),
  aircraft_class: z.string().nullable(),
  width_m: z.coerce.number().positive(),
  length_m: z.coerce.number().positive(),
  height_m: z.coerce.number().positive(),
  model_type: z.enum(['procedural', 'gltf']),
  model_url: z.string().nullable(),
  procedural_shape: z.string().nullable(),
  procedural_color: z.string().nullable(),
  collision_shape: z.enum(['box', 'circle', 'polygon']).default('box'),
  is_active: z.boolean().default(true),
  is_premium: z.boolean().default(false),
  sort_order: z.coerce.number().int().default(0),
  tags: z.array(z.string()).default([]),
  gear_type: z.string().nullable(),
  ground_angle_deg: z.coerce.number().nullable(),
})

type FormValues = z.infer<typeof _schema>

type Option = { value: string; label: string }

const SHAPE_OPTIONS: Option[] = (Object.keys(SHAPE_LABEL) as ProceduralShape[])
  .map((s) => ({ value: s, label: SHAPE_LABEL[s] }))

const AIRCRAFT_CLASS_OPTIONS: Option[] = AIRCRAFT_CLASSES
  .map((c) => ({ value: c, label: `${AIRCRAFT_CLASS_LABEL[c]} — ${TIER_LABELS[CLASS_TIER[c]]}` }))

/** Radix Select can't carry an empty value, so "no class" needs a sentinel. */
const UNTAGGED = '__untagged__'

const GEAR_OPTIONS: Option[] = [
  { value: 'tricycle', label: 'Tricycle' },
  { value: 'taildragger', label: 'Taildragger (tailwheel)' },
  { value: 'tandem', label: 'Tandem' },
  { value: 'skid', label: 'Skid' },
  { value: 'float', label: 'Float' },
]

/**
 * `model_settings.gear` from the form. The gear describes the GLB's pose, so a
 * manual edit is tagged `source: 'manual'` unless the stored value is unchanged.
 */
function gearFromForm(values: FormValues, stored: CatalogGear | undefined): CatalogGear | undefined {
  if (!values.gear_type) return undefined
  const angle = values.ground_angle_deg
  const next: CatalogGear = {
    type: values.gear_type as GearLayout,
    ...(angle != null && Number.isFinite(angle) && angle > 0 ? { ground_angle_deg: angle } : {}),
  }
  const unchanged = stored?.type === next.type && stored?.ground_angle_deg === next.ground_angle_deg
  return { ...next, source: unchanged ? stored?.source : 'manual' }
}

/**
 * The options plus the row's stored value when it isn't one of them — otherwise
 * the Select renders blank for a value the form still holds, and the editor
 * looks like it will save something other than what the row has.
 */
function withStored(options: Option[], stored: string | null | undefined): Option[] {
  return stored && !options.some((o) => o.value === stored)
    ? [...options, { value: stored, label: stored }]
    : options
}

function storedGear(row: { model_settings?: unknown } | null | undefined): CatalogGear | undefined {
  const gear = (row?.model_settings as { gear?: CatalogGear } | undefined)?.gear
  return typeof gear?.type === 'string' ? gear : undefined
}

function toKebab(s: string) {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')
}

export function CatalogItemEditor() {
  const { itemId } = useParams<{ itemId: string }>()
  const navigate = useNavigate()
  const isNew = !itemId

  const { data: existing, isLoading } = useAdminCatalogItem(itemId)
  const createItem = useCreateCatalogItem()
  const updateItem = useUpdateCatalogItem()

  const form = useForm<FormValues>({
    defaultValues: {
      asset_code: '',
      display_name: '',
      name: '',
      manufacturer: null,
      source: 'hangarfit',
      category: 'aircraft',
      subcategory: null,
      aircraft_class: null,
      width_m: 1,
      length_m: 1,
      height_m: 1,
      model_type: 'procedural',
      model_url: null,
      procedural_shape: 'box',
      procedural_color: '#6366F1',
      collision_shape: 'box',
      is_active: true,
      is_premium: false,
      sort_order: 0,
      tags: [],
      gear_type: null,
      ground_angle_deg: null,
    },
  })

  useEffect(() => {
    if (existing) {
      form.reset({
        asset_code: existing.asset_code,
        display_name: existing.display_name,
        name: existing.name,
        manufacturer: existing.manufacturer ?? null,
        source: existing.source,
        category: existing.category as FormValues['category'],
        subcategory: existing.subcategory ?? null,
        aircraft_class: existing.aircraft_class ?? null,
        width_m: existing.width_m,
        length_m: existing.length_m,
        height_m: existing.height_m,
        model_type: existing.model_type as FormValues['model_type'],
        model_url: existing.model_url ?? null,
        procedural_shape: existing.procedural_shape ?? null,
        procedural_color: existing.procedural_color ?? null,
        collision_shape: (existing.collision_shape as FormValues['collision_shape']) ?? 'box',
        is_active: existing.is_active,
        is_premium: existing.is_premium,
        sort_order: existing.sort_order,
        tags: existing.tags ?? [],
        gear_type: storedGear(existing)?.type ?? null,
        ground_angle_deg: storedGear(existing)?.ground_angle_deg ?? null,
      })
    }
  }, [existing, form])

  const onSubmit = async (values: FormValues) => {
    const { gear_type: _gearType, ground_angle_deg: _groundAngle, ...columns } = values
    const { gear: _storedGear, ...otherSettings } = (existing?.model_settings ?? {}) as Record<string, unknown>
    const gear = gearFromForm(values, storedGear(existing))
    const payload = {
      ...columns,
      name: values.name || values.display_name,
      thumbnail_url: existing?.thumbnail_url ?? null,
      model_settings: gear ? { ...otherSettings, gear } : otherSettings,
    }

    if (isNew) {
      await createItem.mutateAsync(payload as Parameters<typeof createItem.mutateAsync>[0])
      navigate('/catalog')
    } else {
      await updateItem.mutateAsync({ id: itemId!, updates: payload as Parameters<typeof updateItem.mutateAsync>[0]['updates'] })
      navigate('/catalog')
    }
  }

  if (!isNew && isLoading) {
    return <Skeleton className="h-96 w-full" />
  }

  const displayName = form.watch('display_name')
  const aircraftClass = form.watch('aircraft_class')
  const proceduralShape = form.watch('procedural_shape')
  const gearType = form.watch('gear_type')
  const isProxy = Boolean(existing?.sub_id)

  return (
    <div>
      <div className="mb-4">
        <Link to="/catalog" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft className="h-4 w-4" /> Back to Catalog
        </Link>
      </div>

      <PageHeader title={isNew ? 'New Catalog Item' : `Edit: ${existing?.display_name}`} />

      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <div className="grid gap-6 md:grid-cols-2">
          {/* Identity */}
          <Card>
            <CardHeader><CardTitle className="text-base">Identity</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Display Name *</Label>
                <Input {...form.register('display_name')} onChange={(e) => {
                  form.setValue('display_name', e.target.value)
                  if (isNew) {
                    form.setValue('asset_code', toKebab(e.target.value))
                    form.setValue('name', e.target.value)
                  }
                }} />
              </div>
              <div className="space-y-2">
                <Label>Asset Code *</Label>
                <Input {...form.register('asset_code')} placeholder={toKebab(displayName || 'my-item')} />
              </div>
              <div className="space-y-2">
                <Label>Manufacturer</Label>
                <Input {...form.register('manufacturer')} />
              </div>
              <div className="space-y-2">
                <Label>Source</Label>
                <Select value={form.watch('source')} onValueChange={(v) => form.setValue('source', v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="hangarfit">HangarMax</SelectItem>
                    <SelectItem value="planemax">PlaneMAX</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Classification */}
          <Card>
            <CardHeader><CardTitle className="text-base">Classification</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Category *</Label>
                <Select value={form.watch('category')} onValueChange={(v) => form.setValue('category', v as FormValues['category'])}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="aircraft">Aircraft</SelectItem>
                    <SelectItem value="vehicle">Vehicle</SelectItem>
                    <SelectItem value="equipment">Equipment</SelectItem>
                    <SelectItem value="furniture">Furniture</SelectItem>
                    <SelectItem value="structural">Structural</SelectItem>
                    <SelectItem value="custom">Custom</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Subcategory</Label>
                <Input {...form.register('subcategory')} placeholder="e.g., single_engine_piston" />
              </div>
              {form.watch('category') === 'aircraft' && (
                <div className="space-y-2">
                  <Label>Aircraft Class</Label>
                  <Select
                    value={aircraftClass ?? UNTAGGED}
                    onValueChange={(v) => form.setValue('aircraft_class', v === UNTAGGED ? null : v)}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value={UNTAGGED}>— Untagged (falls back to Premium)</SelectItem>
                      {withStored(AIRCRAFT_CLASS_OPTIONS, aircraftClass).map((o) => (
                        <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    Decides the lowest plan that can place this aircraft. The app reads it from the CDN
                    catalog JSON (<code>ac</code>), so a change goes live only after the catalog is
                    re-exported (<code>scripts/export-catalog-json.mjs</code>).
                  </p>
                </div>
              )}
              <div className="space-y-2">
                <Label>Collision Shape</Label>
                <Select value={form.watch('collision_shape')} onValueChange={(v) => form.setValue('collision_shape', v as FormValues['collision_shape'])}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="box">Box</SelectItem>
                    <SelectItem value="circle">Circle</SelectItem>
                    <SelectItem value="polygon">Polygon</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Dimensions */}
          <Card>
            <CardHeader><CardTitle className="text-base">Dimensions (meters)</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Width</Label>
                  <Input type="number" step="0.01" {...form.register('width_m')} />
                </div>
                <div className="space-y-2">
                  <Label>Length</Label>
                  <Input type="number" step="0.01" {...form.register('length_m')} />
                </div>
                <div className="space-y-2">
                  <Label>Height</Label>
                  <Input type="number" step="0.01" {...form.register('height_m')} />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Model */}
          <Card>
            <CardHeader><CardTitle className="text-base">Model</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Model Type *</Label>
                <Select value={form.watch('model_type')} onValueChange={(v) => form.setValue('model_type', v as FormValues['model_type'])}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="procedural">Procedural</SelectItem>
                    <SelectItem value="gltf">GLTF</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {form.watch('model_type') === 'procedural' ? (
                <>
                  <div className="space-y-2">
                    <Label>Shape</Label>
                    <Select value={proceduralShape ?? 'box'} onValueChange={(v) => form.setValue('procedural_shape', v)}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {withStored(SHAPE_OPTIONS, proceduralShape).map((o) => (
                          <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Color</Label>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        value={form.watch('procedural_color') ?? '#6366F1'}
                        onChange={(e) => form.setValue('procedural_color', e.target.value)}
                        className="h-8 w-8 cursor-pointer rounded border"
                      />
                      <Input
                        value={form.watch('procedural_color') ?? ''}
                        onChange={(e) => form.setValue('procedural_color', e.target.value)}
                        className="flex-1"
                      />
                    </div>
                  </div>
                </>
              ) : (
                <div className="space-y-2">
                  <Label>Model URL</Label>
                  <Input {...form.register('model_url')} placeholder="https://cdn.example.com/model.glb" />
                </div>
              )}
              {form.watch('category') === 'aircraft' && form.watch('model_type') === 'gltf' && (
                isProxy ? (
                  <p className="text-xs text-muted-foreground">
                    Gear comes from the substitute model ({existing?.sub_name ?? 'substitute'}) — edit it there.
                  </p>
                ) : (
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Gear (as modelled)</Label>
                      <Select
                        value={gearType ?? UNTAGGED}
                        onValueChange={(v) => form.setValue('gear_type', v === UNTAGGED ? null : v)}
                      >
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value={UNTAGGED}>— Unknown</SelectItem>
                          {withStored(GEAR_OPTIONS, gearType).map((o) => (
                            <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    {gearType === 'taildragger' && (
                      <div className="space-y-2">
                        <Label>Ground angle (°)</Label>
                        <Input type="number" step="0.5" min="0" max="25" placeholder="11"
                          {...form.register('ground_angle_deg', { setValueAs: (v) => (v === '' || v == null ? null : Number(v)) })} />
                      </div>
                    )}
                    <p className="col-span-2 text-xs text-muted-foreground">
                      Taildragger enables the editor's ground ↔ level attitude control (pitch 0 … ground angle,
                      blank = 11°) and keeps this model from being a proxy for non-taildraggers. Reaches the
                      app after a catalog re-export (<code>g</code> on the model entry).
                    </p>
                  </div>
                )
              )}
            </CardContent>
          </Card>

          {/* Flags */}
          <Card className="md:col-span-2">
            <CardHeader><CardTitle className="text-base">Flags</CardTitle></CardHeader>
            <CardContent>
              <div className="flex items-center gap-8">
                <label className="flex items-center gap-2">
                  <Switch
                    checked={form.watch('is_active')}
                    onCheckedChange={(v) => form.setValue('is_active', v)}
                  />
                  <span className="text-sm">Active</span>
                </label>
                <label className="flex items-center gap-2">
                  <Switch
                    checked={form.watch('is_premium')}
                    onCheckedChange={(v) => form.setValue('is_premium', v)}
                  />
                  <span className="text-sm">Premium</span>
                </label>
                <div className="flex items-center gap-2">
                  <Label>Sort Order</Label>
                  <Input type="number" className="w-20" {...form.register('sort_order')} />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="flex justify-end gap-3">
          <Button type="button" variant="outline" onClick={() => navigate('/catalog')}>
            Cancel
          </Button>
          <Button type="submit" disabled={createItem.isPending || updateItem.isPending}>
            {(createItem.isPending || updateItem.isPending) && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            <Save className="mr-2 h-4 w-4" />
            {isNew ? 'Create Item' : 'Save Changes'}
          </Button>
        </div>
      </form>
    </div>
  )
}
